<template>
  <div>
    <v-dialog :value="shown" transition="shown-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary" fixed>
          <v-toolbar-title class="ml-4">
            {{
              isReset
                ? $t('priceBulkManagement.resetPricesTitle')
                : $t('priceBulkManagement.overwritePrice')
            }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn class="ml-2 float-right" icon @click="closeDialog()">
            <i class="pl-2 fas fa-times mr-2 text--white" />
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-row>
            <v-col v-if="amount === 0">
              <v-alert
                border="left"
                colored-border
                outlined
                color="red"
              >
              {{ $t('priceBulkManagement.overrideWithZero') }}
              </v-alert>
            </v-col>
          </v-row>
          <v-row v-if="!onlyShowWarning">
            <v-col>
              <v-alert
                border="left"
                colored-border
                outlined
                color="primary accent-4"
              >
                <div v-if="isReset">
                  <span>{{ $t('priceBulkManagement.priceReset') }}</span>
                </div>
                <div v-else>
                  <span v-if="isAbsolute">{{
                    $t('priceBulkManagement.absoluteChange')
                  }}</span>
                  <span v-else>{{
                    $t('priceBulkManagement.priceChange')
                  }}</span>
                  <span v-if="isAbsolute"
                    >: {{ (amount * 100) | currency }}</span
                  >
                  <span v-else>: {{ amount }}%</span>
                </div>
              </v-alert>
            </v-col>
          </v-row>
          <v-row v-if="!onlyShowWarning">
            <v-col cols="12">
              <v-text-field
                v-model="filterText"
                outlined
                dense
                :label="$t('priceBulkManagement.filter')"
                single-line
                hide-details
              >
                <template #append>
                  <span class="fa fa-filter pt-1"></span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12">
              <v-data-table
                :headers="headers"
                :items="dataReady ? pricePredictions : []"
                :items-per-page="20"
                :loading="$asyncComputed.pricePredictions.updating"
                :footer-props="{
                  itemsPerPageText: $t('general.rowsPerPage'),
                  itemsPerPageAllText: $t('dataFooter.itemsPerPageAllText'),
                }"
                :locale="$store.getters.getLanguageLocale().substr(0, 2)"
                :loading-text="$t('priceBulkManagement.calculating')"
                :search="filterText"
              >
                <template #[`item.priceDifference`]="{ item }">
                  <v-chip
                    small
                    outlined
                    :color="getBadgeColor(item.priceDifference)"
                  >
                    <i
                      v-if="item.priceDifference !== 0"
                      :class="`fas mr-1 fa-arrow-${
                        item.priceDifference > 0 ? 'up' : 'down'
                      }`"
                    />
                    {{ item.priceDifference }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="d-flex justify-end">
              <v-btn color="primary" outlined @click="submitPriceChanges">
                {{
                  isReset
                    ? $t('priceBulkManagement.resetPricesTitle')
                    : $t('priceBulkManagement.overwritePrice')
                }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { calculateCustomPrices, calculateResetPrices } from './CustomPrice'
import _ from 'lodash'
import definitions from '../../../definitions'
import moment from 'moment'

/**
 * Component to inform the
 * user how the new prices
 * will look likfe after
 * a bulk price overwrite
 *
 * It shows as well which
 * prices will be reset
 */
export default {
  name: 'PriceOverrideConfirmPopUp',

  props: {
    /**
     * boolean which defines if the action is
     *  resetting prices to their default
     */
    isReset: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * boolean which defines
     * if the overlay popup
     * is shown to the user
     */
    shown: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The product definitions we are
     * going to overwrite the prices
     * for
     */
    productDefinitions: {
      type: Array,
      default: () => {
        return []
      },
      required: true,
    },
    /**
     * The dates we overwrite
     * the prices for
     */
    overwriteDates: {
      type: Array,
      default: () => {
        return []
      },
      required: true,
    },
    /**
     * defines if we overwrite
     * the prices in a absoulte
     * or relative manner
     */
    isAbsolute: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * The amount that the
     * prices change:
     * depends on the
     * isAbsolute value
     */
    amount: {
      type: Number,
      required: false,
      default: 0,
    },
    /**
     * the current posted prices
     * for the selected product definitions
     */
    currentSelectedPrices: {
      required: true,
      type: Array,
      default: () => {
        return []
      },
    },
    onlyShowWarning: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      headers: [
        {
          text: this.$t('priceBulkManagement.product'),
          value: 'productDefinitionTranslation',
        },
        {
          text: 'Date',
          align: 'start',
          value: 'date',
        },
        { text: this.$t('priceBulkManagement.oldPrice'), value: 'oldPrice' },
        { text: this.$t('priceBulkManagement.newPrice'), value: 'newPrice' },
        {
          text: this.$t('priceBulkManagement.priceDifference'),
          value: 'priceDifference',
        },
      ],
      filterText: null,
    }
  },

  computed: {
    dataReady() {
      return this.pricePredictions?.length
    },
  },

  asyncComputed: {
    /**
     * Calculate the price
     * changes and bring them
     * in a vuetify table form
     * array
     */
    async pricePredictions() {
      if (!this.shown || this.onlyShowWarning) return {}
      const data = this.isReset
        ? await calculateResetPrices(
            [...this.productDefinitions],
            [...this.currentSelectedPrices],
            [...this.overwriteDates]
          )
        : await calculateCustomPrices(
            [...this.productDefinitions],
            [...this.currentSelectedPrices],
            [...this.overwriteDates],
            this.isAbsolute,
            this.amount
          )
      // formatting is needed to ensure filtering works correctly based on the shown values
      const lang = this.$store.getters.getLanguageLocale().substr(0, 2)
      const formatted = _.map(data, (row) => {
        row.date = moment(row.date).locale(lang).format('DD.MM.YY - dd', lang)
        row.productDefinitionTranslation = this.$t(
          row.productDefinitionTranslation
        )
        row.oldPrice = this.$options.filters.currency(row.oldPrice, false)
        row.newPrice = this.$options.filters.currency(row.newPrice, false)
        row.priceDifference = this.$options.filters.currency(
          row.priceDifference,
          false
        )
        return row
      })
      return formatted
    },
  },

  methods: {
    /**
     * the modal has been closed
     * notify the parent
     */
    closeDialog() {
      this.$emit('closed')
    },
    /**
     * Confirm the price changes
     */
    submitPriceChanges() {
      this.$emit('closed')
      this.$emit('ok')
    },
    isPriceDifferencePositive(priceDiff) {
      try {
        parseInt(priceDiff)
        return priceDiff > 0
      } catch (e) {
        return false
      }
    },
    getBadgeColor(priceDiff) {
      if (this.isPriceDifferencePositive(priceDiff)) {
        // blue
        return definitions.colors.dipolar[0]
      }
      // red
      return definitions.colors.dipolar[1]
    },
  },
}
</script>
